// src/pages/Forms/index.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  CircularProgress,
  Alert,
  ButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  Tabs,
  Tab
} from '@mui/material';
import {
  Description as FormIcon,
  Assignment as ConsentIcon,
  LocalHospital as MedicalIcon,
  MedicalServices as TreatmentIcon,
  Assessment as AssessmentIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';

// Hooks
import { useFormCategories, useRecentFormSubmissions, useFormSubmissions } from '../../hooks/useForms';
import DevOnly from '../../components/DevOnly';
import { formatClientName, formatFormCategory, formatFormName } from '../../utils/displayNames';

const FormsManagementHub = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState(0); // 0 = Templates, 1 = Submissions
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch form categories with template counts
  const { 
    data: categories = [], 
    isLoading: categoriesLoading, 
    error: categoriesError 
  } = useFormCategories();

  // Fetch recent form submissions for the preview
  const { 
    data: recentSubmissions = [], 
    isLoading: recentLoading 
  } = useRecentFormSubmissions(5);

  // Fetch all form submissions with pagination and search
  const { 
    data: submissionsData, 
    isLoading: submissionsLoading 
  } = useFormSubmissions({
    page: page + 1,
    limit: rowsPerPage,
    category: selectedCategory === 'all' ? undefined : selectedCategory,
    search: searchTerm || undefined
  });

  const formSubmissions = submissionsData?.submissions || [];
  const totalSubmissions = submissionsData?.total || 0;

  const getCategoryIcon = (iconType) => {
    switch (iconType) {
      case 'consent':
        return <ConsentIcon sx={{ fontSize: 40 }} />;
      case 'medical':
        return <MedicalIcon sx={{ fontSize: 40 }} />;
      case 'treatment':
        return <TreatmentIcon sx={{ fontSize: 40 }} />;
      case 'assessment':
        return <AssessmentIcon sx={{ fontSize: 40 }} />;
      default:
        return <FormIcon sx={{ fontSize: 40 }} />;
    }
  };

  const handleCategoryClick = (categoryId) => {
    navigate(`/forms/category/${categoryId}`);
  };

  const handleViewSubmission = (submission) => {
    // Navigate to form detail page to view the submission
    if (submission.id) {
      navigate(`/forms/submission/${submission.id}`);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleCategoryFilterChange = (categoryId) => {
    setSelectedCategory(categoryId);
    setPage(0);
  };

  const handleCreateForm = () => {
    navigate('/forms/create');
  };

  const formatSubmissionTime = (dateString) => {
    if (!dateString) return 'Unknown time';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (error) {
      return 'Invalid date';
    }
  };

  if (categoriesError) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load forms data. Please try again later.
        </Alert>
      </Box>
    );
  }

  // Calculate total templates from categories
  const totalTemplates = categories.reduce((sum, cat) => sum + (cat.templates || cat.forms || 0), 0);

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Forms Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your medical forms, view submissions, and track completion rates
          </Typography>
          <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
            <Chip 
              label={`${totalTemplates} Form Templates`} 
              color="primary" 
              variant="outlined"
            />
            <Chip 
              label={`${totalSubmissions} Client Submissions`} 
              color="success" 
              variant="outlined"
            />
          </Box>
        </Box>
        <DevOnly>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateForm}
            size="large"
          >
            Create Form
          </Button>
        </DevOnly>
      </Box>

      {/* Search Bar */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search forms..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Tabs for Templates vs Submissions */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label={`Form Templates (${totalTemplates})`} />
          <Tab label={`Client Submissions (${totalSubmissions})`} />
        </Tabs>
      </Paper>

      {/* Category Filter */}
      <Box sx={{ mb: 3 }}>
        <ButtonGroup variant="outlined" size="small">
          <Button
            variant={selectedCategory === 'all' ? 'contained' : 'outlined'}
            onClick={() => handleCategoryFilterChange('all')}
          >
            All Categories
          </Button>
          {categories.map((category) => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? 'contained' : 'outlined'}
              onClick={() => handleCategoryFilterChange(category.id)}
            >
              {category.name}
            </Button>
          ))}
        </ButtonGroup>
      </Box>

      {/* Templates Tab Content */}
      {activeTab === 0 && (
        <>
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {categoriesLoading ? (
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              </Grid>
            ) : (
              categories
                .filter(category => selectedCategory === 'all' || category.id === selectedCategory)
                .map((category) => (
                  <Grid item xs={12} sm={6} md={3} key={category.id}>
                    <Card 
                      elevation={2}
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease-in-out',
                        '&:hover': {
                          elevation: 4,
                          transform: 'translateY(-2px)'
                        }
                      }}
                      onClick={() => handleCategoryClick(category.id)}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 3 }}>
                        <Box sx={{ 
                          color: `${category.color}.main`,
                          mb: 2,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          {getCategoryIcon(category.icon)}
                        </Box>
                        <Typography variant="h6" gutterBottom>
                          {category.name}
                        </Typography>
                        {category.description && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, minHeight: '2.5em' }}>
                            {category.description}
                          </Typography>
                        )}
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="h4" color="primary">
                            {category.forms || category.templates || 0}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Templates
                          </Typography>
                          <Chip 
                            label={`${category.submissions || 0} submissions`}
                            size="small"
                            color="success"
                            sx={{ mt: 1 }}
                          />
                          {typeof category.recent_submissions === 'number' && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {category.recent_submissions} in last 30 days
                            </Typography>
                          )}
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          color={category.color || 'primary'}
                          fullWidth
                        >
                          View Templates
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))
            )}
          </Grid>
        </>
      )}

      {/* Submissions Tab Content */}
      {activeTab === 1 && (
        <Paper elevation={2}>
          <Box sx={{ p: 3 }}>
            {/* Search bar for submissions */}
            <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                placeholder="Search client submissions by name, form, or client..."
                value={searchTerm}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ maxWidth: 600 }}
              />
            </Box>
            {submissionsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : formSubmissions.length === 0 ? (
              <Alert severity="info">
                No form submissions found{selectedCategory !== 'all' ? ' for this category' : ''}.
              </Alert>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Form Name</TableCell>
                        <TableCell>Client</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell>Submitted Date</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formSubmissions.map((submission) => (
                        <TableRow key={submission.id} hover>
                          <TableCell>
                            <Typography variant="body2">
                              {formatFormName(submission)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PersonIcon sx={{ fontSize: 16 }} color="action" />
                              <Typography variant="body2">
                                {formatClientName(submission)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {formatFormCategory(submission) !== 'N/A' && (
                              <Chip
                                label={formatFormCategory(submission)}
                                size="small"
                                variant="outlined"
                              />
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" color="text.secondary">
                              {formatSubmissionTime(submission.submitted_date)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={submission.status || 'Completed'}
                              size="small"
                              color={submission.status === 'pending' ? 'warning' : 'success'}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() => handleViewSubmission(submission)}
                              size="small"
                              color="primary"
                            >
                              <ViewIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={totalSubmissions}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default FormsManagementHub;
